
import { useState, useCallback, useRef } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { withPlaceNameLeading } from '@/lib/geo/placeName';

interface CacheItem<T> {
  data: T;
  timestamp: number;
}

// 30 minutes in milliseconds. Module-level so it's a stable reference for
// the `withCache` callback below (no need to list it as a dependency).
const CACHE_TTL = 30 * 60 * 1000;

export interface LocationFeature {
  text: string;
  place_name: string;
  category?: string;
  center?: [number, number];
}

export interface SuggestionOptions {
  /** [lng, lat] to bias ranking toward. Omitted means rank globally. */
  proximity?: [number, number];
  /** ISO 3166-1 alpha-2 codes, comma separated, to restrict results to. */
  country?: string;
}

export interface GeocodeOptions {
  /**
   * Whether a geocoding failure raises a toast. Defaults to true, which is
   * right for a form the user is typing into. Pass `false` from a background
   * or polled caller: `geocodeLocation` answers [0, 0] on failure and its
   * callers are expected to treat that as "no coordinates", so the toast is
   * the only part that is not already handled.
   */
  notify?: boolean;
}

// Geocoding, reverse-geocoding and address suggestions all run server-side via
// the `location` edge function (Mapbox-backed) — no client Mapbox token needed.
export const useGeocoding = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Create a cache with 30-minute expiration
  const cacheRef = useRef<Map<string, CacheItem<unknown>>>(new Map());

  // Helper function to get/set cache
  const withCache = useCallback(async <T,>(key: string, fetchFn: () => Promise<T>): Promise<T> => {
    const now = Date.now();
    const cached = cacheRef.current.get(key);

    if (cached && now - cached.timestamp < CACHE_TTL) {
      return cached.data as T;
    }

    const data = await fetchFn();
    cacheRef.current.set(key, { data, timestamp: now });
    return data;
  }, []);

  const geocodeLocation = useCallback(async (
    query: string,
    { notify = true }: GeocodeOptions = {}
  ): Promise<[number, number]> => {
    if (!query.trim()) {
      throw new Error('Search query is required for geocoding');
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const cacheKey = `geocode:${query}`;
      
      return await withCache(cacheKey, async () => {
        // Use Supabase Edge Function instead of directly calling Mapbox
        const { data, error: fnError } = await supabase.functions.invoke('location', {
          body: { 
            action: 'geocode',
            params: { query } 
          }
        });
        
        if (fnError) {
          console.error('Geocoding function error:', fnError);
          throw new Error(`Geocoding function error: ${fnError.message}`);
        }
        
        if (data?.features && data.features.length > 0) {
          return data.features[0].center as [number, number];
        }
        
        throw new Error(`Could not geocode location: ${query}`);
      });
    } catch (err) {
      console.error(`Error geocoding ${query}:`, err);
      setError(err instanceof Error ? err.message : `Failed to geocode: ${query}`);
      if (notify) {
        toast({
          title: "Geocoding Error",
          description: err instanceof Error ? err.message : `Failed to geocode: ${query}`,
          variant: "destructive",
        });
      }
      // Return a fallback coordinate to avoid breaking the app
      return [0, 0];
    } finally {
      setIsLoading(false);
    }
  }, [toast, withCache]);

  // Updated reverse geocoding to use Edge Function with caching
  const reverseGeocode = useCallback(async (coords: [number, number]): Promise<string> => {
    try {
      setIsLoading(true);
      setError(null);
      
      const [lng, lat] = coords;
      const cacheKey = `reverseGeocode:${lng},${lat}`;
      
      return await withCache(cacheKey, async () => {
        // Use Supabase Edge Function
        const { data, error: fnError } = await supabase.functions.invoke('location', {
          body: { 
            action: 'reverseGeocode',
            params: { 
              coordinates: [lng, lat] 
            } 
          }
        });
        
        if (fnError) {
          console.error('Reverse geocoding function error:', fnError);
          throw new Error(`Reverse geocoding error: ${fnError.message}`);
        }
        
        if (data?.features && data.features.length > 0) {
          return data.features[0].place_name;
        }
        
        return "Unknown location";
      });
    } catch (err) {
      console.error('Error reverse geocoding:', err);
      setError(err instanceof Error ? err.message : 'Failed to get address from coordinates');
      return "Location unavailable";
    } finally {
      setIsLoading(false);
    }
  }, [withCache]);

  /**
   * Address suggestions for an autocomplete field.
   *
   * `proximity` biases ranking toward a point — pass the rider's location or
   * their already-chosen pickup. Without it Mapbox ranks by global prominence,
   * which is how "30th street station" typed in Philadelphia came back as
   * Sacramento, Fort Worth and a flyover in India. It only reorders results;
   * a genuine match far away is still returned.
   */
  const getAddressSuggestions = useCallback(async (
    query: string,
    { proximity, country }: SuggestionOptions = {},
  ): Promise<LocationFeature[]> => {
    if (!query || query.length < 2) {
      return [];
    }

    try {
      setIsLoading(true);
      setError(null);

      // The bias changes the answer, so it belongs in the cache key.
      const cacheKey = `suggestions:${query}|${proximity?.join(',') ?? ''}|${country ?? ''}`;
      
      return await withCache(cacheKey, async () => {
        // Use Supabase Edge Function with retry logic
        const maxRetries = 3;
        let retryCount = 0;
        let lastError;
        
        while (retryCount < maxRetries) {
          try {
            const { data, error: fnError } = await supabase.functions.invoke('location', {
              body: {
                action: 'geocode',
                params: { query, proximity, country }
              }
            });

            if (fnError) {
              throw fnError;
            }

            if (data?.features && data.features.length > 0) {
              return data.features.map((feature: { text: string; place_name: string; center: [number, number]; properties?: { category?: string } }) => ({
                text: feature.text,
                // A row the rider clicks must fill the field with the place
                // they clicked on. See withPlaceNameLeading — Search Box's
                // full_address is not reliably name-first.
                place_name: withPlaceNameLeading(feature.text ?? '', feature.place_name ?? ''),
                category: feature.properties?.category,
                // Kept, not dropped: the caller uses the chosen pickup's centre
                // to bias the destination field, which costs no extra request.
                center: feature.center,
              }));
            }
            
            return [];
          } catch (err) {
            lastError = err;
            retryCount++;
            
            if (retryCount < maxRetries) {
              // Wait with exponential backoff before retrying
              await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 500));
            }
          }
        }
        
        // If we got here, all retries failed
        console.error('Address suggestions function error after retries:', lastError);
        throw new Error(`Address suggestions error: ${lastError?.message || 'Unknown error'}`);
      });
    } catch (err) {
      console.error('Error getting address suggestions:', err);
      setError(err instanceof Error ? err.message : 'Failed to get address suggestions');
      // Return empty array instead of throwing to prevent UI disruption
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [withCache]);

  return {
    geocodeLocation,
    reverseGeocode,
    getAddressSuggestions,
    isLoading,
    error
  };
};
